import React, { useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Box, CssBaseline } from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'

// Layout
import AppLayout from './components/layout/AppLayout'

// Page Components
import AdminDashboard from './pages/AdminDashboard'
import TLDashboard from './pages/TLDashboard'
import TrainerDashboard from './pages/TrainerDashboard'
import Schools from './pages/Schools'
import DemoSchools from './pages/DemoSchools'
import BilledSchools from './pages/BilledSchools'
import NotBilledSchools from './pages/NotBilledSchools'
import ExpiredSchools from './pages/ExpiredSchools'
import Training from './pages/Training'
import Analytics from './pages/Analytics'
import Contracts from './pages/Contracts'
import Renewals from './pages/Renewals'
import Support from './pages/Support'
import Settings from './pages/Settings'
import Login from './pages/Login'
import WhatsAppMessaging from './pages/WhatsAppMessaging'
import WhatsAppQueue from './pages/WhatsAppQueue'
import WhatsAppInbox from './pages/WhatsAppInbox'
import SchoolAccounts from './pages/SchoolAccounts'
import LoginLogs from './pages/LoginLogs'
import MyOrders from './pages/MyOrders'
import SchoolQuery from './pages/SchoolQuery'
import SchoolGroups from './pages/SchoolGroups'
import TelecallerManagement from './pages/TelecallerManagement'
import AIAgents from './pages/AIAgents'
import ReferralManagement from './pages/ReferralManagement'
import PromoterManagement from './pages/PromoterManagement'

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { WhatsAppEventsProvider } from './contexts/WhatsAppEventsContext'

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(true)

  // Wait for auth check to complete
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '1.2rem',
          color: '#666',
        }}
      >
        Loading...
      </Box>
    )
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  const toggleSidebar = () => setSidebarOpen((prev) => !prev)

  return (
    <>
      <CssBaseline />
      <WhatsAppEventsProvider enabled={isAuthenticated}>
        <AppLayout sidebarOpen={sidebarOpen} onSidebarToggle={toggleSidebar}>
          <AnimatePresence mode="wait">
            <Routes>
              <Route
                path="/"
                element={<Navigate to="/login" replace />}
              />
              <Route
                path="/login"
                element={<Login />}
              />
              <Route
                path="/dashboard"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AdminDashboard />
                  </motion.div>
                }
              />
              <Route
                path="/dashboard/tl"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TLDashboard />
                  </motion.div>
                }
              />
              <Route
                path="/dashboard/trainer"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TrainerDashboard />
                  </motion.div>
                }
              />
              <Route
                path="/schools"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Schools />
                  </motion.div>
                }
              />
              <Route
                path="/schools/demo"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <DemoSchools />
                  </motion.div>
                }
              />
              <Route
                path="/schools/billed"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <BilledSchools />
                  </motion.div>
                }
              />
              <Route
                path="/schools/not-billed"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <NotBilledSchools />
                  </motion.div>
                }
              />
              <Route
                path="/schools/expired"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ExpiredSchools />
                  </motion.div>
                }
              />
              <Route
                path="/training"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Training />
                  </motion.div>
                }
              />
              <Route
                path="/analytics"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Analytics />
                  </motion.div>
                }
              />
              <Route
                path="/contracts"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Contracts />
                  </motion.div>
                }
              />
              <Route
                path="/renewals"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Renewals />
                  </motion.div>
                }
              />
              <Route
                path="/support"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Support />
                  </motion.div>
                }
              />
              <Route
                path="/settings"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Settings />
                  </motion.div>
                }
              />
              <Route
                path="/whatsapp"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <WhatsAppMessaging />
                  </motion.div>
                }
              />
              <Route
                path="/whatsapp/queue"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <WhatsAppQueue />
                  </motion.div>
                }
              />
              <Route
                path="/whatsapp/inbox"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <WhatsAppInbox />
                  </motion.div>
                }
              />
              <Route
                path="/school-accounts"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SchoolAccounts />
                  </motion.div>
                }
              />
              <Route
                path="/login-logs"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <LoginLogs />
                  </motion.div>
                }
              />
              <Route
                path="/my-orders"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <MyOrders />
                  </motion.div>
                }
              />
              <Route
                path="/school-query"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SchoolQuery />
                  </motion.div>
                }
              />
              <Route
                path="/school-groups"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SchoolGroups />
                  </motion.div>
                }
              />
              <Route
                path="/telecaller"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TelecallerManagement />
                  </motion.div>
                }
              />
              <Route
                path="/ai-agents"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AIAgents />
                  </motion.div>
                }
              />
              <Route
                path="/referrals"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ReferralManagement />
                  </motion.div>
                }
              />
              <Route
                path="/promoters"
                element={
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <PromoterManagement />
                  </motion.div>
                }
              />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AnimatePresence>
        </AppLayout>
      </WhatsAppEventsProvider>
    </>
  )
}

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
